import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
} from "@/shared/lib/componentHierarchy";

export type CatalogItemModel = Omit<
  Extract<CatalogEvent, { type: "catalog/item-synchronized" }>,
  "type"
>;

export type CatalogItemState = CatalogItemModel & {
  readonly isHovered: boolean;
  readonly isMenuOpen: boolean;
  readonly isNgRevealed: boolean;
  readonly isR18Revealed: boolean;
  readonly isR18Hidden: boolean;
  readonly isImageMasked: boolean;
  readonly longPressOpensMenu: boolean;
  readonly isMenuButtonDisplayed: boolean;
};

export type CatalogItemEvent = Extract<
  CatalogEvent,
  {
    type:
      | "catalog/item-synchronized"
      | "catalog/item-hover-changed"
      | "catalog/item-menu-opened"
      | "catalog/item-menu-closed"
      | "catalog/item-menu-toggled"
      | "catalog/item-ng-revealed"
      | "catalog/item-r18-revealed"
      | "catalog/item-activated"
      | "catalog/item-prefetch-requested";
  }
>;

export type CatalogItemEffect =
  | {
      readonly type: "mark-viewed";
      readonly boardSlug: string;
      readonly threadId: string;
    }
  | { readonly type: "prefetch"; readonly threadId: string };

export function isCatalogItemEvent(
  event: CatalogEvent,
): event is CatalogItemEvent {
  return event.type.startsWith("catalog/item-");
}

export function createCatalogItemMediator(
  initialModel: CatalogItemModel,
  runEffect: (effect: CatalogItemEffect) => void = (): void => undefined,
): Mediator<CatalogItemState, CatalogItemEvent, CatalogItemEffect> {
  return createMediator<CatalogItemState, CatalogItemEvent, CatalogItemEffect>(
    projectState(initialModel, false, false, false, false),
    transitionCatalogItem,
    { runEffect },
  );
}

export function transitionCatalogItem(
  state: CatalogItemState,
  event: CatalogItemEvent,
): MediatorTransitionResult<CatalogItemState, CatalogItemEffect> {
  switch (event.type) {
    case "catalog/item-synchronized": {
      const threadChanged = event.threadId !== state.threadId;
      return {
        status: "handled",
        state: projectState(
          event,
          threadChanged ? false : state.isHovered,
          threadChanged ? false : state.isMenuOpen,
          threadChanged ? false : state.isNgRevealed,
          threadChanged ? false : state.isR18Revealed,
        ),
      };
    }
    case "catalog/item-hover-changed":
      return {
        status: "handled",
        state: { ...state, isHovered: event.hovered },
      };
    case "catalog/item-menu-opened":
      return { status: "handled", state: { ...state, isMenuOpen: true } };
    case "catalog/item-menu-closed":
      return { status: "handled", state: { ...state, isMenuOpen: false } };
    case "catalog/item-menu-toggled":
      return {
        status: "handled",
        state: { ...state, isMenuOpen: !state.isMenuOpen },
      };
    case "catalog/item-ng-revealed":
      return {
        status: "handled",
        state: projectState(
          state,
          state.isHovered,
          state.isMenuOpen,
          true,
          state.isR18Revealed,
        ),
      };
    case "catalog/item-r18-revealed":
      return {
        status: "handled",
        state: projectState(
          state,
          state.isHovered,
          state.isMenuOpen,
          state.isNgRevealed,
          true,
        ),
      };
    case "catalog/item-activated":
      if (state.isNg && !state.isNgRevealed) {
        return {
          status: "handled",
          state: projectState(
            state,
            state.isHovered,
            state.isMenuOpen,
            true,
            state.isR18Revealed,
          ),
        };
      }
      return state.trackViewed
        ? {
            status: "handled",
            state,
            effects: [
              {
                type: "mark-viewed",
                boardSlug: state.boardSlug,
                threadId: state.threadId,
              },
            ],
          }
        : { status: "handled", state };
    case "catalog/item-prefetch-requested":
      return state.prefetchAvailable && !state.isImageMasked
        ? {
            status: "handled",
            state,
            effects: [{ type: "prefetch", threadId: state.threadId }],
          }
        : { status: "handled", state };
  }
}

function projectState(
  model: CatalogItemModel,
  isHovered: boolean,
  isMenuOpen: boolean,
  isNgRevealed: boolean,
  isR18Revealed: boolean,
): CatalogItemState {
  const isR18Hidden =
    model.isR18 && model.hasAttachment && !model.showR18 && !isR18Revealed;
  const isImageMasked = (model.isNg && !isNgRevealed) || isR18Hidden;
  const longPressOpensMenu =
    model.menuOpenMethod === "long-press" ||
    (model.menuOpenMethod === "auto" && !model.hasFinePointer);
  const isMenuButtonDisplayed =
    model.menuOpenMethod === "menu-button" ||
    (model.menuOpenMethod === "auto" && model.hasFinePointer);
  return {
    ...model,
    isHovered,
    isMenuOpen,
    isNgRevealed,
    isR18Revealed,
    isR18Hidden,
    isImageMasked,
    longPressOpensMenu,
    isMenuButtonDisplayed,
  };
}
